<script>
	//@ts-nocheck
	import { stack } from 'd3-shape';
	import { scaleLinear, scaleBand } from 'd3-scale';
	import { union, index, max } from 'd3-array';
	import { format } from 'd3-format';
	// import colors from '$lib/data/viz/colors.json';

	// import roundToCeilingSignificantDigits from '$lib/utils/roundToCeilingSignificantDigits';

	export let data = [];

	// d3 donut chart
	export let width = 500;
	export let showLabels = false;
	export let title = '';
	export let labelSizeEm = 0.7;
	export const tooltipEnabled = false;
	export let defaultOpacity = 0.5;
	export let formatNumber = (d) => {
		console.log('d', d)
		return format('.0f')(d * 100) + '%'; // format to whole number
	}
	export let getValueForDisplay = (d) => d / 100;
	export const showLegend = true;

	// export let sortOrder = 'descending';

	// const tooltipFadeOpacity = 0.5;

	$: {
		// console.log('data', data)
		// console.log('series', series)
	}

	const barHeight = 20;
	const labelHeight = 15; // Height for labels under bars

	const margin = 0;
	const marginBottom = 0;
	$: marginTop = title ? margin + 0 : margin;
	const marginLeft = margin;
	const marginRight = margin;

	$: height = barHeight + labelHeight + marginBottom + marginTop;

	const color = (category) => {
		// console.log(category)
		// TODO: add colors
    // return a random color
    // return `#${Math.floor(Math.random()*16777215).toString(16)}`;
		let color = data.find((d) => {
			// console.log(d.category, category, d.category.toLowerCase() === category.toLowerCase())
			return d.category.toLowerCase() === category.toLowerCase()
		});

		// console.log(color)
		return color.color || '#000000';
	};

	// Function to determine if a color is dark or light
	// Returns true if color is dark, false if light
	const isDarkColor = (hexColor) => {
		// Remove # if present
		const hex = hexColor.replace('#', '');
		
		// Parse RGB values
		const r = parseInt(hex.substring(0, 2), 16) / 255;
		const g = parseInt(hex.substring(2, 4), 16) / 255;
		const b = parseInt(hex.substring(4, 6), 16) / 255;
		
		// Calculate relative luminance using WCAG formula
		const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
		
		// Return true if dark (luminance < 0.5), false if light
		return luminance < 0.5;
	};

	// Function to get appropriate text color based on background color
	const getTextColor = (backgroundColor) => {
		return isDarkColor(backgroundColor) ? 'white' : 'black';
	};

	// Determine the series that need to be stacked.  
	const series = stack()
		.keys(union(data.map((d) => d.category))) // distinct series keys, in input order
		.value(([, D], key) => {
			return D?.get(key).value;
		})(
		// get value for each series key and stack
		index(
			data,
			() => 'default',
			(d) => d.category
		)
	); // group by stack then series key

	// Determine the maximum value of the stacked series.
	const maxValue = max(series, (d) => max(d, (d) => d[1]));

	// Calculate total value using getValueForDisplay
	$: totalValue = series.length > 0 
		? series.reduce((sum, s) => {
			return sum + s.reduce((seriesSum, item) => {
				const value = item[1] - item[0];
				return seriesSum + (getValueForDisplay(value) || 0);
			}, 0);
		}, 0)
		: 0;

	// Check if total value is zero (including when maxValue is 0 or undefined, or data is empty)
	$: isZeroTotal = (totalValue === 0 || !maxValue || maxValue === 0) && (series.length > 0 || data.length === 0);

	// use d3-format to format a number to have comma separators
	// Prepare the scales for positional and color encodings.
	$: xScale = scaleLinear()
		.domain([0, maxValue || 1])
		.range([marginLeft, width - marginRight]);

	$: yScale = scaleBand()
		.domain(series.map((d) => (d?.key ? d.key : 'default')))
		.range([height - 10, 0]);

	let hoveredData = null;

	$: getOpacity = (d) => {
		const hoveredSeries = hoveredData?.element;
		const currentSeries = d.key;
		if (hoveredSeries) {
			return hoveredSeries === currentSeries ? 1 : defaultOpacity;
		} else {
			return 1;
		}
	};

	$: {
		// console.log('width', width);
		console.log('series', series);
		// console.log('maxValue', maxValue);
	}
</script>

<div class="relative w-full horizontal-barchart-wrapper">
	<svg {width} height={ title ? height+30 : height} class="horizontal-barchart-svg">
		<g>
			{#if title}
			<text
				x={width / 2}
				y={'1em'}
				text-anchor="middle"
				font-size={`${labelSizeEm}em`}
				opacity={defaultOpacity}
			>
				{title}
			</text>
			{/if}
			{#if isZeroTotal}
				<!-- Show gray bar when total value is zero -->
				<g transform={title ? '' : 'translate(0, -20)'}>
					<rect
						x={marginLeft}
						y={yScale.range()[0] || 0}
						width={width - marginLeft - marginRight}
						height={barHeight}
						fill="#808080"
						fill-opacity={0.7}
						stroke="#808080"
						stroke-width={0}
					/>
				</g>
			{:else if series?.length > 0}
				<g
				transform={title ? '' : 'translate(0, -20)'}
				>
					{#each series as s}
						<!-- svelte-ignore a11y-no-static-element-interactions -->
						<!-- svelte-ignore a11y-mouse-events-have-key-events -->
						{#each s as item}
							<rect
								x={xScale(item[0])}
								y={yScale.range()[0]}
								width={xScale(item[1]) - xScale(item[0])}
								height={barHeight}
								fill={color(s.key)}
								fill-opacity={getOpacity(s)}
								stroke={color(s.key)}
								stroke-width={0}
								stroke-opacity={defaultOpacity}
								on:mouseover={() => {
									hoveredData = {
										element: s.key,
										value: getValueForDisplay(item[1] - item[0]),
										x: (xScale(item[0]) + xScale(item[1])) / 2 + 5,
										y: barHeight + (yScale.range()[1] + yScale.range()[0]) / 2
									};
								}}
								on:mouseout={() => {
									hoveredData = null;
								}}
							/>
							{#if xScale(item[1]) - xScale(item[0]) > 20}
								<text
									x={(xScale(item[0]) + xScale(item[1])) / 2}
									y={yScale.range()[0] + barHeight / 2}
									text-anchor="middle"
									dy={`${labelSizeEm / 2}em`}
									font-size={`${labelSizeEm}em`}
									fill={getTextColor(color(s.key))}
									opacity={1}
									style="pointer-events: none;"
								>
									{formatNumber(item[1] - item[0])}
								</text>
							{/if}
							<!-- Series name label under the bar -->
							{#if showLabels && xScale(item[1]) - xScale(item[0]) > 80}
							<text
								x={xScale(item[0])}
								y={yScale.range()[0] + barHeight + 12}
								font-size={`${labelSizeEm * 0.8}em`}
								fill={'black'}
								opacity={0.8}
								style="pointer-events: none;"
							>
								{s.key}
							</text>
							{/if}
						{/each}
					{/each}
				</g>
			{/if}
		</g>
	</svg>


</div>

<style>
	.horizontal-barchart-wrapper {
		width: 100%;
		max-width: 100%;
		overflow: hidden;
	}
	
	.horizontal-barchart-svg {
		width: 100%;
		max-width: 100%;
		height: auto;
		display: block;
	}
	
	/* Ensure SVG scales on mobile */
	@media (max-width: 767px) {
		.horizontal-barchart-wrapper {
			width: 100%;
		}
		
		.horizontal-barchart-svg {
			width: 100%;
			max-width: 100%;
		}
	}
</style>
